import { Card, Flex, Skeleton } from "antd"
import React, { memo, useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"

import {
  TABULAR,
  homeCardStyle,
  homeColor,
  homeText
} from "../../home-theme.ts"
import { ChevronRightIcon } from "../home-icons.tsx"
import { useAppDispatch } from "../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import { invoicesHiddenForRole } from "../../../../utils/invoices-visibility.ts"
import { type AppDispatch } from "../../../../store.ts"
import { selectActiveBranchId } from "../../../Branches/redux/branches.slice.ts"
import { ACTIVE_STATUSES } from "../../../Projects/constants/project-statuses.ts"
import { projectsSetGridFilters } from "../../../Projects/redux/projects.slice.ts"
import { type PermissionFeatures } from "../../../SettingsPermissions/types/permissions.types.ts"
import { fetchDashboardStats } from "../../redux/dashboard.thunks.ts"
import { type DashboardStats as DashboardStatsType } from "../../types/types.ts"
import {
  DASHBOARD_STATS_ITEMS,
  type DashboardStatItemConfig,
  type DashboardStatType,
  readStatValue
} from "./dashboard-stats-config.tsx"

const beforeNavigateDispatchActions: Partial<
  Record<DashboardStatType, Parameters<AppDispatch>[0]>
> = {
  projects: projectsSetGridFilters({
    project_status: ACTIVE_STATUSES.join(",")
  })
}

interface DashboardStatsProps {
  permissionFeatures: PermissionFeatures
}

export const DashboardStats = memo(
  ({ permissionFeatures }: DashboardStatsProps) => {
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const { t } = useTranslation()
    const roleId = useAppSelector((state) => state.auth.userInfo?.role?.id)
    const invoicesHidden = invoicesHiddenForRole(roleId)
    const activeBranchId = useAppSelector(selectActiveBranchId)

    const [data, setData] = useState<DashboardStatsType | null>(null)
    const [loading, setLoading] = useState(false)

    const handleGetStats = useCallback(async () => {
      try {
        setLoading(true)
        const res = await dispatch(
          fetchDashboardStats({ team: activeBranchId })
        ).unwrap()
        setData(res)
      } finally {
        setLoading(false)
      }
    }, [dispatch, activeBranchId])

    const handleClick = function (stat: DashboardStatItemConfig) {
      const action = beforeNavigateDispatchActions?.[stat?.key]
      if (action) dispatch(action)
      navigate(stat?.href as string)
    }

    const visibleStats = useMemo(() => {
      if (!permissionFeatures) return []
      return DASHBOARD_STATS_ITEMS.filter(
        (item) =>
          item.isVisible(permissionFeatures) &&
          !(item.key === "invoices" && invoicesHidden)
      )
    }, [permissionFeatures, invoicesHidden])

    useEffect(() => {
      void handleGetStats()
    }, [handleGetStats])

    return (
      <div className="home-shortcuts-wrap">
        <div className="home-shortcuts">
          {loading
            ? Array.from({ length: visibleStats.length }).map((_, i) => (
                <Skeleton.Node key={i} className="w-full h-[62px]" />
              ))
            : visibleStats.map((stat) => (
                <Card
                  key={stat.key}
                  onClick={stat.href ? () => handleClick(stat) : undefined}
                  hoverable={!!stat.href}
                  variant="borderless"
                  style={homeCardStyle}
                  classNames={{ body: "p-3.5" }}
                >
                  <Flex align="center" gap={11}>
                    <Flex
                      align="center"
                      justify="center"
                      className="size-10 shrink-0"
                      style={{
                        borderRadius: 9,
                        backgroundColor: stat.iconBg,
                        color: stat.iconColor
                      }}
                    >
                      {stat.icon}
                    </Flex>
                    {/* Label and count read as one block beside the tile. */}
                    <div className="min-w-0">
                      <div
                        style={{
                          fontSize: 14,
                          lineHeight: "19px",
                          fontWeight: 600,
                          color: homeColor.ink
                        }}
                      >
                        {stat.title}
                      </div>
                      <div
                        data-testid={`stat-value-${stat.key}`}
                        style={{
                          ...homeText.label,
                          ...TABULAR,
                          color: homeColor.inkMuted
                        }}
                      >
                        {readStatValue(data, stat) ??
                          t("widget.empty", "No data")}
                      </div>
                    </div>
                    {stat.href ? (
                      <ChevronRightIcon
                        width={16}
                        height={16}
                        style={{
                          marginLeft: "auto",
                          flex: "none",
                          color: homeColor.inkMuted
                        }}
                      />
                    ) : null}
                  </Flex>
                </Card>
              ))}
        </div>
      </div>
    )
  }
)

DashboardStats.displayName = "DashboardStats"
